import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Switch,
  IconButton,
  Typography,
} from "@mui/material";
import {
  activatePlan,
  deactivatePlan,
  isAuthenticated,
} from "../../../ApiService/ApiService";
import {
  BorderColor,
  EditNote,
  Payment,
  Paid,
  AccessTime,
  AdjustTwoTone,
} from "@mui/icons-material";
import "../../../Styles/dashboard.css";
import CustomDialog from "../../../Components/CustomDialog";

const headCells = [
  {
    id: "name",
    label: "Plan Name",
    icon: <Payment sx={{ marginRight: 1 }} />,
  },
  {
    id: "duration",
    label: "Duration",
    icon: <AccessTime sx={{ marginRight: 1 }} />,
  },
  {
    id: "amount",
    label: "Amount",
    icon: <Paid sx={{ marginRight: 1 }} />,
  },
  {
    id: "status",
    label: "Status",
    icon: <AdjustTwoTone sx={{ marginRight: 1 }} />,
  },
  {
    id: "edit",
    label: "Edit",
    icon: <EditNote sx={{ marginRight: 1 }} />,
  },
];

function PlansTable({ plans, fetchPlans }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [paginatedData, setPaginatedData] = useState([]);
  const [dialog, setDialog] = useState({
    visible: false,
    title: "",
    message: "",
    buttons: [],
  });

  useEffect(() => {
    setPaginatedData(plans?.data);
  }, [plans]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDialogClose = () => {
    setDialog({ ...dialog, visible: false });
  };

  const handleChangeStatus = async (id, currentStatus) => {
    try {
      if (isAuthenticated()) {
        const response = currentStatus
          ? await deactivatePlan(id)
          : await activatePlan(id);
        // console.log("RES", response);
        if (response) {
          if (response?.data?.status === 200) {
            const updatedPlansData = paginatedData?.map((plan) =>
              plan.id === id ? { ...plan, isActive: !currentStatus } : plan
            );
            setDialog({
              visible: true,
              title: "Success",
              message: "Status updated successfully",
              buttons: [{ name: "OK", onPress: handleDialogClose }],
            });
            setPaginatedData(updatedPlansData);
            fetchPlans();
          } else {
            const errorMessage =
              response?.data?.errorMessage || "Something went wrong";
            setDialog({
              visible: true,
              title: "Error",
              message: errorMessage,
              buttons: [{ name: "Close", onPress: handleDialogClose }],
            });
          }
        } else {
          setDialog({
            visible: true,
            title: "Error",
            message: "Invalid response from the server",
            buttons: [{ name: "Close", onPress: handleDialogClose }],
          });
        }
      } else {
        setDialog({
          visible: true,
          title: "Unauthorized action",
          message: "Please login and try again",
          buttons: [{ name: "Close", onPress: handleDialogClose }],
        });
      }
    } catch (error) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Something went wrong",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
    }
  };

  const slicedData = paginatedData?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <>
      <CustomDialog
        visible={dialog.visible}
        title={dialog.title}
        message={dialog.message}
        buttons={dialog.buttons}
        onClose={handleDialogClose}
      />
      <Box>
        <Paper>
          <TableContainer>
            <Table aria-labelledby="tableTitle">
              <TableHead>
                <TableRow>
                  {headCells.map((headCell) => (
                    <TableCell key={headCell.id} padding="normal">
                      <Box className="table-head">
                        {headCell.icon}
                        {headCell.label}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {slicedData?.length > 0 ? (
                  slicedData?.map((row) => (
                    <TableRow key={row?.id}>
                      <TableCell
                        component="th"
                        scope="row"
                        className="table-cell"
                      >
                        {row.name}
                      </TableCell>
                      <TableCell className="table-cell">{`${row.duration} Days`}</TableCell>
                      <TableCell className="table-cell">{row.amount}</TableCell>
                      <TableCell>
                        <Switch
                          checked={row.isActive}
                          color="success"
                          sx={{
                            "& .MuiSwitch-track": {
                              backgroundColor: !row.isActive
                                ? "#ff0000"
                                : "#4caf50",
                            },
                          }}
                          onClick={() =>
                            handleChangeStatus(row.id, row.isActive)
                          }
                          inputProps={{ "aria-label": "controlled" }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton aria-label="edit">
                          <BorderColor />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={2}>
                      <Typography variant="body2" color="textSecondary">
                        No data available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {slicedData?.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[10]}
              component="div"
              count={paginatedData?.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Paper>
      </Box>
    </>
  );
}

PlansTable.propTypes = {
  plans: PropTypes.object.isRequired,
  fetchPlans: PropTypes.func.isRequired,
};

export default PlansTable;
